import { useState } from 'react'
import { toast } from 'sonner'

import type {
    CarrierManualUnits,
    CarrierReceiverPatch,
    CarrierShippingFeePayer,
} from '@/lib/api/carrier-shipment-api'
import type { CarrierManualUnitsInput } from '@/components/carrier/carrier-manual-units-fields'
import type { CarrierReceiverValue } from '@/components/carrier/carrier-receiver-fields'
import type { CustomerShippingService } from '@/components/carrier/carrier-service-options'
import type { CarrierQuoteContext } from '@/lib/carrier-quote-fingerprint'

import {
    CarrierCODField,
    parseCODOverride,
} from '@/components/carrier/carrier-cod-field'
import { describeCarrierFailure } from '@/components/carrier/carrier-form-failure'
import { CarrierLegacyUnitRow } from '@/components/carrier/carrier-legacy-unit-row'
import { CarrierManualUnitsFields } from '@/components/carrier/carrier-manual-units-fields'
import { CarrierReceiverFields } from '@/components/carrier/carrier-receiver-fields'
import { CarrierServiceOptions } from '@/components/carrier/carrier-service-options'
import { useCarrierLegacyUnit } from '@/components/carrier/use-carrier-legacy-unit'
import { useCarrierQuoteState } from '@/components/carrier/use-carrier-quote-state'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
    useCarrierQuote,
    useDispatchCarrierShipment,
} from '@/hooks/use-carrier-shipment-query'
import { m } from '@/lib/paraglide/messages'
import { formatVND } from '@/lib/format'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'

interface Props {
    orderId: string
    active?: boolean
    quoteContext: CarrierQuoteContext
    initialShippingFeePayer?: CarrierShippingFeePayer
    /** Receiver as the order holds it; when given, the block is shown and
     *  edits are written back to the order on booking. */
    receiver?: CarrierReceiverValue
    /** False once the order is paid or out for delivery. */
    receiverEditable?: boolean
    /** Đơn quà ship trả trước (§14.21): phí ship người đặt đã chuyển khoản.
     *  Server không cộng cước vào COD, kể cả khi chọn người nhận trả cước. */
    prepaidShippingFee?: number
    /** Service the customer picked on the web / Mini App order: pre-selected
     *  when quoted, flagged when staff book another one. */
    customerService?: CustomerShippingService
    onCreated?: () => void
}

/** Settlement books at most the order's outstanding debt, so a typed amount
 *  above it leaves the excess off the customer's ledger — or, on a paid
 *  order, collects twice. Say so before booking. */
function codAboveDebtWarnings(
    typed: number | undefined,
    outstanding: number,
): string[] {
    if (typed === undefined || typed <= outstanding) return []
    if (outstanding === 0) {
        return [
            m.carrier_form_cod_override_order_paid({
                amount: formatVND(typed),
            }),
        ]
    }
    return [
        m.carrier_form_cod_override_above_debt({
            debt: formatVND(outstanding),
            excess: formatVND(typed - outstanding),
        }),
    ]
}

/** Only the fields that differ from the order are edits worth sending. */
function receiverPatch(
    initial: CarrierReceiverValue | undefined,
    current: CarrierReceiverValue,
): CarrierReceiverPatch {
    if (!initial) return {}
    const patch: CarrierReceiverPatch = {}
    if (current.name.trim() !== initial.name)
        patch.receiver_name = current.name.trim()
    if (current.phone.trim() !== initial.phone)
        patch.receiver_phone = current.phone.trim()
    if (current.address.trim() !== initial.address)
        patch.receiver_address = current.address.trim()
    if ((current.provinceCode ?? '') !== (initial.provinceCode ?? '')) {
        patch.shipping_province_code = current.provinceCode ?? ''
        patch.shipping_province = current.provinceName ?? ''
    }
    if ((current.wardCode ?? '') !== (initial.wardCode ?? '')) {
        patch.shipping_ward_code = current.wardCode ?? ''
        patch.shipping_ward = current.wardName ?? ''
    }
    return patch
}

export function CarrierShipmentForm({
    orderId,
    active = true,
    quoteContext,
    initialShippingFeePayer = 'company',
    receiver: initialReceiver,
    receiverEditable = false,
    prepaidShippingFee,
    customerService,
    onCreated,
}: Props) {
    const workflow = useWorkflowTracking('carrier_dispatch', {
        active,
        attemptKey: orderId,
        operation: 'create',
    })
    const quote = useCarrierQuote(orderId)
    const dispatch = useDispatchCarrierShipment(orderId)
    const [weight, setWeight] = useState('')
    const [packageCount, setPackageCount] = useState('1')
    const [note, setNote] = useState<string>(m.carrier_form_default_note())
    const [shippingFeePayer, setShippingFeePayer] =
        useState<CarrierShippingFeePayer>(initialShippingFeePayer)
    const [receiver, setReceiver] = useState<CarrierReceiverValue>(
        initialReceiver ?? {
            name: '',
            phone: '',
            address: quoteContext.shippingAddress,
            provinceCode: quoteContext.shippingProvinceCode ?? undefined,
            provinceName: quoteContext.shippingProvince ?? undefined,
            wardCode: quoteContext.shippingWardCode ?? undefined,
            wardName: quoteContext.shippingWard ?? undefined,
        },
    )
    const [codOverride, setCodOverride] = useState('')
    const [codError, setCodError] = useState<string | null>(null)
    const [manual, setManual] = useState<CarrierManualUnitsInput | null>(null)
    const [quoteError, setQuoteError] = useState<string | null>(null)
    // Touched flags surface a field's validation message only after the
    // operator tried to act — the submit/quote buttons themselves stay
    // clickable so the message is what tells them why nothing happened.
    const [weightTouched, setWeightTouched] = useState(false)
    const [serviceTouched, setServiceTouched] = useState(false)
    const weightGram = Number(weight)
    const weightValid = Number.isInteger(weightGram) && weightGram > 0
    const weightError =
        weightTouched && !weightValid ? m.carrier_form_weight_invalid() : null
    const packageCountValue = Number(packageCount)
    const packageCountError =
        Number.isInteger(packageCountValue) && packageCountValue >= 1
            ? null
            : m.carrier_form_package_count_invalid()
    const manualUnits = (): CarrierManualUnits | undefined => {
        if (!manual) return undefined
        const provinceId = Number(manual.provinceId)
        const wardId = Number(manual.wardId)
        if (!provinceId || !wardId) return undefined
        return { vtp_province_id: provinceId, vtp_ward_id: wardId }
    }
    // The quote is priced for the receiver as currently typed, so an address
    // edit after quoting stales the quote the same way an order edit does.
    const effectiveQuoteContext: CarrierQuoteContext = initialReceiver
        ? {
              ...quoteContext,
              shippingAddress: receiver.address,
              shippingProvince: receiver.provinceName ?? null,
              shippingProvinceCode: receiver.provinceCode ?? null,
              shippingWard: receiver.wardName ?? null,
              shippingWardCode: receiver.wardCode ?? null,
          }
        : quoteContext
    const patch = receiverPatch(initialReceiver, receiver)
    // The suggestion follows receiver edits not yet saved on the order.
    const legacyUnit = useCarrierLegacyUnit({
        orderId,
        active,
        wardCode: receiver.wardCode ?? '',
        editedWardCode: patch.shipping_ward_code,
        editedAddress: patch.receiver_address,
    })
    const legacyWardId = legacyUnit.wardId
    const quoteState = useCarrierQuoteState({
        quoteContext: effectiveQuoteContext,
        weightGram,
        manualUnits: manualUnits(),
        legacyWardId,
        preferredServiceCode: customerService?.code,
    })
    const selectedService = quoteState.services?.find(
        (service) => service.service_code === quoteState.serviceCode,
    )
    const serviceMissing = !quoteState.selectedServiceQuoted || !selectedService
    const serviceError =
        serviceTouched && serviceMissing
            ? m.carrier_form_service_required()
            : null
    const goodsCOD =
        quoteContext.collectCod === true
            ? Math.max(quoteContext.totalAmount - quoteContext.paidAmount, 0)
            : 0
    // The typed override is the goods amount, never the final collection —
    // Viettel Post adds the freight on top when the recipient pays it.
    const codParsed = parseCODOverride(codOverride)
    const goodsAmount = codParsed.amount ?? goodsCOD
    const shippingPrepaid = prepaidShippingFee !== undefined
    // Khớp đúng COD server gửi hãng: đơn ship trả trước không cộng cước.
    const freightToRecipient =
        shippingFeePayer === 'customer' && !shippingPrepaid
    const collectionTotal =
        selectedService && freightToRecipient
            ? goodsAmount + selectedService.fee
            : goodsAmount
    const codSummaryTotal = m.carrier_form_cod_summary_total({
        amount: formatVND(collectionTotal),
    })
    const codSummaryDetail = selectedService
        ? freightToRecipient && quoteContext.collectCod !== true
            ? m.carrier_form_cod_summary_freight_only({
                  fee: formatVND(selectedService.fee),
              })
            : freightToRecipient
              ? m.carrier_form_cod_summary_breakdown_customer({
                    goods: formatVND(goodsAmount),
                    fee: formatVND(selectedService.fee),
                })
              : m.carrier_form_cod_summary_breakdown_company({
                    fee: formatVND(selectedService.fee),
                })
        : m.carrier_form_cod_summary_pending()
    // A typed amount that already equals goods + freight is usually staff
    // adding the freight themselves out of habit — Viettel Post adds it too,
    // which would double it, so flag it (non-blocking; they may mean it).
    const freightLooksDoubled =
        freightToRecipient &&
        selectedService !== undefined &&
        codParsed.amount !== undefined &&
        codParsed.amount === goodsCOD + selectedService.fee
    const codWarnings = [
        ...(freightLooksDoubled
            ? [m.carrier_form_cod_override_freight_already_added()]
            : []),
        ...codAboveDebtWarnings(
            codParsed.amount,
            Math.max(quoteContext.totalAmount - quoteContext.paidAmount, 0),
        ),
    ]
    // Goods already paid: the courier may still collect the freight, and
    // sales must see that figure before booking.
    const showsCollection =
        quoteContext.collectCod === true ||
        (freightToRecipient && selectedService !== undefined)
    const submitLabel = showsCollection
        ? m.carrier_form_submit_with_amount({
              amount: formatVND(collectionTotal),
          })
        : m.carrier_form_submit()
    const handleFailure = (err: unknown, isQuote: boolean) => {
        if (legacyUnit.showRejection(err)) return
        const outcome = describeCarrierFailure(err, isQuote)
        if (outcome.kind === 'manual') {
            setManual({ provinceId: outcome.provinceId, wardId: '' })
            return
        }
        setQuoteError(outcome.inline ? outcome.message : null)
        toast.error(outcome.message)
    }
    const runQuote = async () => {
        // The button stays clickable even with no weight typed yet; the field
        // message is what tells the operator why nothing happened.
        workflow.step('review_opened')
        setQuoteError(null)
        setWeightTouched(true)
        if (!weightValid) {
            workflow.step('validation_failed')
            return
        }
        quoteState.startQuote()
        try {
            const units = manualUnits()
            const fingerprint = quoteState.currentFingerprint
            const options = await quote.mutateAsync({
                weight_gram: weightGram,
                ...patch,
                ...(units ? { manual_vtp_units: units } : {}),
                ...legacyUnit.payload,
            })
            quoteState.accept(options, fingerprint)
        } catch (err) {
            handleFailure(err, true)
        }
    }
    const runDispatch = async () => {
        // Submit stays clickable so an operator sees why the booking is
        // refused; the field message is the explanation, not a greyed button.
        setCodError(codParsed.error)
        setWeightTouched(true)
        setServiceTouched(true)
        const legacyChosen = legacyUnit.requireChoice()
        if (
            codParsed.error ||
            packageCountError ||
            !weightValid ||
            serviceMissing ||
            !legacyChosen
        ) {
            workflow.step('validation_failed')
            return
        }
        if (!selectedService) return
        try {
            const units = manualUnits()
            await workflow.run(
                () =>
                    dispatch.mutateAsync({
                        service_code: quoteState.serviceCode,
                        weight_gram: weightGram,
                        package_count: packageCountValue,
                        shipping_fee_payer: shippingFeePayer,
                        // Inspection costs the co-op extra with Viettel Post, so
                        // bookings never request it; the co-op's own box label
                        // still tells the courier the recipient may inspect.
                        allow_inspection: false,
                        ...(codParsed.amount !== undefined
                            ? { cod_amount: codParsed.amount }
                            : {}),
                        ...patch,
                        ...(note ? { note } : {}),
                        ...(units ? { manual_vtp_units: units } : {}),
                        ...legacyUnit.payload,
                    }),
                (result) => result?.id,
            )
            onCreated?.()
        } catch (err) {
            handleFailure(err, false)
        }
    }
    const selectService = (serviceCode: string) => {
        workflow.step('selection_changed')
        quoteState.selectService(serviceCode)
    }
    return (
        <div className="space-y-4">
            {initialReceiver && (
                <CarrierReceiverFields
                    value={receiver}
                    onChange={(value) => {
                        workflow.interact('details')
                        setReceiver(value)
                    }}
                    editable={receiverEditable}
                />
            )}
            <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="carrier-weight">
                    {m.carrier_form_weight()} *
                </label>
                <Input
                    id="carrier-weight"
                    type="number"
                    min={1}
                    value={weight}
                    onChange={(e) => {
                        workflow.interact('details')
                        setWeight(e.target.value)
                    }}
                />
                <p className="text-muted-foreground text-xs">
                    {m.carrier_form_weight_hint()}
                </p>
                {weightError && (
                    <p className="text-destructive text-sm" role="alert">
                        {weightError}
                    </p>
                )}
            </div>
            <div className="space-y-1">
                <label
                    className="text-sm font-medium"
                    htmlFor="carrier-package-count"
                >
                    {m.carrier_form_package_count()} *
                </label>
                <Input
                    id="carrier-package-count"
                    type="number"
                    min={1}
                    step={1}
                    value={packageCount}
                    onChange={(e) => {
                        workflow.interact('items')
                        setPackageCount(e.target.value)
                    }}
                />
                <p className="text-muted-foreground text-xs">
                    {m.carrier_form_package_count_hint()}
                </p>
                {packageCountError && (
                    <p className="text-destructive text-sm" role="alert">
                        {packageCountError}
                    </p>
                )}
            </div>
            <CarrierLegacyUnitRow
                unit={legacyUnit}
                orderId={orderId}
                wardCode={receiver.wardCode ?? ''}
                provinceCode={receiver.provinceCode}
                wardName={receiver.wardName ?? quoteContext.shippingWard}
                onWardChange={
                    initialReceiver && receiverEditable
                        ? (ward) =>
                              setReceiver({
                                  ...receiver,
                                  wardCode: ward.code,
                                  wardName: ward.name,
                              })
                        : undefined
                }
                onInteract={() => workflow.interact('selection')}
            />
            {manual && (
                <CarrierManualUnitsFields
                    value={manual}
                    onChange={(value) => {
                        workflow.interact('selection')
                        setManual(value)
                    }}
                />
            )}
            <Button
                variant="outline"
                size="sm"
                disabled={quote.isPending}
                onClick={() => void runQuote()}
            >
                {m.carrier_form_quote()}
            </Button>
            {quoteError && (
                <p className="text-destructive text-sm" role="alert">
                    {quoteError}
                </p>
            )}
            {quoteState.quoteInvalidated && (
                <p className="text-warning text-sm" role="status">
                    {m.carrier_form_quote_stale()}
                </p>
            )}
            <CarrierServiceOptions
                services={quoteState.services}
                selected={quoteState.serviceCode}
                onSelect={selectService}
                customerService={customerService}
            />
            {serviceError && (
                <p className="text-destructive text-sm" role="alert">
                    {serviceError}
                </p>
            )}
            <div className="space-y-1">
                <label
                    className="text-sm font-medium"
                    htmlFor="carrier-shipping-fee-payer"
                >
                    {m.carrier_form_shipping_fee_payer()}
                </label>
                <select
                    id="carrier-shipping-fee-payer"
                    className="border-input bg-background h-11 w-full rounded-md border px-2.5 text-base md:h-9 md:text-sm"
                    value={shippingFeePayer}
                    onChange={(event) => {
                        workflow.interact('payment')
                        setShippingFeePayer(
                            event.target.value as CarrierShippingFeePayer,
                        )
                    }}
                >
                    <option value="company">
                        {m.carrier_form_shipping_fee_payer_company()}
                    </option>
                    <option value="customer">
                        {m.carrier_form_shipping_fee_payer_customer()}
                    </option>
                </select>
                {shippingPrepaid && (
                    <p className="text-muted-foreground text-sm" role="status">
                        {m.carrier_form_gift_shipping_prepaid({
                            fee: formatVND(prepaidShippingFee),
                            amount: formatVND(collectionTotal),
                        })}
                    </p>
                )}
                {shippingPrepaid && shippingFeePayer === 'customer' && (
                    <p className="text-warning text-sm" role="alert">
                        {m.carrier_form_gift_payer_customer_warning()}
                    </p>
                )}
            </div>
            {quoteContext.collectCod !== true && !shippingPrepaid && (
                <div className="bg-muted space-y-0.5 rounded-md px-3 py-2">
                    <p className="text-lg font-semibold">{codSummaryTotal}</p>
                    <p className="text-muted-foreground text-sm">
                        {codSummaryDetail}
                    </p>
                </div>
            )}
            {quoteContext.collectCod === true && (
                <CarrierCODField
                    summaryTotal={codSummaryTotal}
                    summaryDetail={codSummaryDetail}
                    goodsCOD={goodsCOD}
                    value={codOverride}
                    onChange={(raw) => {
                        workflow.interact('payment')
                        setCodOverride(raw)
                        if (codError) setCodError(parseCODOverride(raw).error)
                    }}
                    error={codError}
                    warnings={codWarnings}
                />
            )}
            <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="carrier-note">
                    {m.carrier_form_note()}
                </label>
                <Textarea
                    id="carrier-note"
                    value={note}
                    onChange={(e) => {
                        workflow.interact('details')
                        setNote(e.target.value)
                    }}
                />
            </div>

            <Button
                disabled={dispatch.isPending}
                onClick={() => void runDispatch()}
            >
                {submitLabel}
            </Button>
        </div>
    )
}
